import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLocaleNavigate, L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { fmtCHF, fmtKM } from "../lib/constants";
import { FEATURE_CATEGORIES } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Heart, MapPin, Phone, Eye, ArrowLeft, Calendar, Gauge, Fuel, Cog, Palette, Zap, Car, Check, MessageSquare } from "lucide-react";
import JsonLd from "../components/JsonLd";
import PriceIndicator from "../components/PriceIndicator";
import SimilarListings from "../components/SimilarListings";
import RecentlyViewed from "../components/RecentlyViewed";
import TrustSection from "../components/TrustSection";
import GalleryLightbox, { GalleryHeroButton } from "../components/GalleryLightbox";
import SocialProofPills from "../components/SocialProofPills";
import { trackEvent } from "../lib/analytics";
import { recordView } from "../lib/recentViews";
import { CompareToggleButton } from "../components/CompareToggleButton";
import { buildVehicleSchema, buildBreadcrumbSchema } from "../lib/schema";
import { useActiveLocale } from "../lib/i18n";
import { useSeoHead } from "../lib/seoHead";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");
const FILE_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useLocaleNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const locale = useActiveLocale();
  const [car, setCar] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [favIds, setFavIds] = useState([]);
  const [msg, setMsg] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  // Phase 66 — guest inquiry fields (used only when !user)
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestHp, setGuestHp] = useState(""); // honeypot — must stay empty

  useEffect(() => {
    api.getListing(id)
      .then((doc) => {
        setCar(doc);
        // Phase 64 — analytics
        trackEvent("listing_view", {
          listing_id: doc.id,
          brand: doc.brand,
          model: doc.model,
          price_chf: doc.price_chf,
          canton: doc.canton,
          seller_type: doc.seller_type,
        });
        // Track recently-viewed: localStorage always + backend opt-in when logged in
        try { recordView(doc); } catch { /* ignore */ }
        if (user) api.recentViewsAdd(doc.id).catch(() => { /* non-fatal */ });
        // Phase 51: complete the push → click → listing-view funnel when arriving via a push.
        try {
          const sp = new URLSearchParams(window.location.search);
          const pushId = sp.get("push_id");
          if (pushId) {
            api.pushTrackListingView({ push_id: pushId, listing_id: doc.id }).catch(() => { /* non-fatal */ });
          }
        } catch { /* ignore */ }
      })
      .catch(() => navigate("/listings"));
    if (user) api.listFavorites().then((favs) => setFavIds(favs.map((f) => f.id))).catch(() => {});
  }, [id, user, navigate]);

  // Phase 55.2 — dynamic OG/Twitter + canonical + product price (rich social/Google).
  // Hook must run before any early return; values become null until car loads.
  const _canonicalUrl = car ? `${SITE_URL}/${locale}/listings/${car.id}` : null;
  const _ogImage = (car && car.images && car.images.length)
    ? `${FILE_BASE}/api/files/${car.images[0]}`
    : (car ? `${SITE_URL}/icons/og-default.png` : null);
  useSeoHead(car ? {
    title: `${car.brand} ${car.model} ${car.year || ""} · ${fmtCHF(car.price_chf)} · CarHive`.replace(/\s+·/g, " ·").trim(),
    description: (car.description || `${car.brand} ${car.model} – ${fmtCHF(car.price_chf)}, ${fmtKM(car.mileage_km)} km, ${car.year}. Provisionsfrei auf CarHive inserieren.`).replace(/\s+/g, " ").slice(0, 160),
    canonical: _canonicalUrl,
    image: _ogImage,
    ogType: "product",
    product: {
      price: Math.round(car.price_chf || 0),
      priceCurrency: "CHF",
      availability: car.status === "active" ? "in stock" : "sold out",
    },
    hreflangs: {
      de: `${SITE_URL}/de/listings/${car.id}`,
      fr: `${SITE_URL}/fr/listings/${car.id}`,
      it: `${SITE_URL}/it/listings/${car.id}`,
      en: `${SITE_URL}/en/listings/${car.id}`,
      "x-default": `${SITE_URL}/de/listings/${car.id}`,
    },
  } : null);

  if (!car) return <div className="flex min-h-[60vh] items-center justify-center text-zinc-500">{t.misc.loading}</div>;

  const isFav = favIds.includes(car.id);
  const isOwner = user && user.id === car.owner_id;
  const canonicalUrl = _canonicalUrl;
  const vehicleSchema = buildVehicleSchema(car, { canonicalUrl, fileBaseUrl: FILE_BASE });
  const breadcrumbSchema = buildBreadcrumbSchema({ siteUrl: SITE_URL, locale, car });

  const toggleFav = async () => {
    if (!user) { toast.error(t.misc.login_required); navigate("/login"); return; }
    if (isFav) { await api.removeFavorite(car.id); setFavIds((p) => p.filter((x) => x !== car.id)); trackEvent("favorite_remove", { listing_id: car.id }); }
    else { await api.addFavorite(car.id); setFavIds((p) => [...p, car.id]); toast.success("Gemerkt"); trackEvent("favorite_add", { listing_id: car.id, brand: car.brand, price_chf: car.price_chf }); }
  };

  const sendMsg = async () => {
    if (isOwner) { toast.error("Eigenes Inserat"); return; }
    if (!msg.trim() || msg.trim().length < 10) {
      toast.error("Bitte schreibe eine Nachricht (min 10 Zeichen)");
      return;
    }
    // Phase 66 — Guest inquiry path (no account required, biggest conversion lift)
    if (!user) {
      const name = guestName.trim();
      const email = guestEmail.trim();
      if (name.length < 2) { toast.error("Bitte Namen eingeben"); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Bitte gültige E-Mail eingeben"); return; }
      setSending(true);
      try {
        await api.sendGuestInquiry(car.id, {
          name, email,
          phone: (phone || "").trim() || undefined,
          message: msg,
          locale,
          hp_field: guestHp,
        });
        toast.success("Anfrage gesendet! Der Verkäufer wird sich melden.");
        trackEvent("message_sent", { listing_id: car.id, brand: car.brand, mode: "guest" });
        setMsg(""); setPhone(""); setGuestName(""); setGuestEmail("");
      } catch (e) {
        const det = e?.response?.data?.detail;
        toast.error(det || "Fehler beim Senden");
      } finally { setSending(false); }
      return;
    }
    setSending(true);
    try {
      const cleanedPhone = (phone || "").trim();
      await api.sendMessage({
        listing_id: car.id,
        recipient_id: car.owner_id,
        content: msg,
        locale,
        phone: cleanedPhone || undefined,
      });
      toast.success("Nachricht gesendet");
      trackEvent("message_sent", {
        listing_id: car.id,
        brand: car.brand,
        canton: car.canton,
        seller_type: car.seller_type,
      });
      setMsg("");
      setPhone("");
      navigate("/messages");
    } catch (e) {
      toast.error("Fehler beim Senden");
    } finally { setSending(false); }
  };

  const images = car.images?.length ? car.images : [];
  const fallback = "https://images.unsplash.com/photo-1761229170508-f4791c297af8?w=1400&q=70&auto=format&fit=crop";

  return (
    <div className="mx-auto max-w-7xl overflow-x-hidden px-4 pb-24 pt-6 sm:px-6 sm:pb-10 sm:pt-8 lg:px-8">
      <JsonLd id={`vehicle-${car.id}`} data={vehicleSchema} />
      <JsonLd id={`breadcrumb-${car.id}`} data={breadcrumbSchema} />
      <GalleryLightbox
        images={images}
        open={lightboxOpen}
        index={activeImg}
        title={car.title}
        label={t.listing.gallery_label}
        onClose={() => setLightboxOpen(false)}
      />
      <button onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> {t.listing.back}
      </button>

      {/* Phase 70.1 — `min-w-0` on grid cells prevents the inner carousel
          (`-mx-4 overflow-x-auto`) from forcing the column to expand beyond
          the viewport on mobile, which broke listing detail width. */}
      <div className="grid gap-10 lg:grid-cols-[1fr_400px]">
        <div className="min-w-0">
          {/* Premium Gallery */}
          <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
            <div
              className="group relative aspect-[16/10] cursor-zoom-in bg-black"
              onClick={() => images.length > 0 && setLightboxOpen(true)}
              data-testid="gallery-hero"
            >
              <img
                src={images[activeImg] ? api.fileUrl(images[activeImg]) : fallback}
                alt={car.title}
                className="h-full w-full object-cover transition group-hover:scale-[1.01]"
                loading="eager"
              />
              {/* Overlay controls (top-right) */}
              <div className="pointer-events-none absolute right-3 top-3 flex items-center gap-2">
                <div className="pointer-events-auto">
                  <GalleryHeroButton count={images.length} label={t.listing.images_count(images.length)} onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }} />
                </div>
              </div>
              {/* Bottom gradient + counter */}
              {images.length > 0 && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/70 to-transparent p-3 pt-12 text-xs text-white">
                  <span data-testid="gallery-hero-counter">{activeImg + 1} / {images.length}</span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto p-2 sm:gap-2 sm:p-3">
                {images.map((p, i) => (
                  <button
                    key={p}
                    onClick={() => setActiveImg(i)}
                    onDoubleClick={() => setLightboxOpen(true)}
                    className={`relative h-14 w-20 flex-shrink-0 overflow-hidden rounded-md border-2 transition sm:h-16 sm:w-24 ${activeImg === i ? "border-red-600" : "border-zinc-800 opacity-60 hover:opacity-100"}`}
                    aria-label={`Bild ${i + 1}`}
                    data-testid={`gallery-thumb-strip-${i}`}
                  >
                    <img src={api.fileUrl(p)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Header */}
          <div className="mt-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-500">{car.brand}</p>
            <h1 className="mt-2 break-words font-display text-3xl font-bold leading-tight text-white sm:text-4xl">{car.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-zinc-400">
              {car.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {car.location}</span>}
              <span className="inline-flex items-center gap-1.5"><Eye className="h-4 w-4" /> {car.views || 0} {t.listing.views}</span>
            </div>
            {/* Phase 61 — Social proof pills (real activity only, hidden when none) */}
            <SocialProofPills listingId={car.id} locale={locale} max={3} />
            {/* Mobile-only price + specs glance pill row */}
            <div className="mt-5 flex items-baseline gap-3 lg:hidden" data-testid="mobile-price-pill">
              <p className="font-display text-3xl font-bold text-red-500">{fmtCHF(car.price_chf)}</p>
              <span className="text-sm text-zinc-500">· {car.year} · {fmtKM(car.mileage_km)} km</span>
            </div>
            {/* Phase 66 — Mobile contact CTA (above-the-fold so users don't scroll past the gallery
                 only to find no action). Hidden on desktop where the sticky sidebar already shows it. */}
            {!isOwner && (
              <div className="mt-4 flex flex-wrap gap-2 lg:hidden" data-testid="mobile-contact-cta">
                <Button
                  asChild
                  className="flex-1 bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400"
                  data-testid="mobile-contact-message"
                >
                  <a href="#inquiry-form">{t.listing.send}</a>
                </Button>
                {car.phone && (
                  <Button
                    asChild
                    variant="outline"
                    className="border-zinc-700 bg-transparent text-white hover:bg-zinc-900"
                    data-testid="mobile-contact-phone"
                  >
                    <a
                      href={`tel:${car.phone}`}
                      onClick={() => trackEvent("phone_click", { listing_id: car.id, source: "listing_mobile_cta" })}
                    >
                      <Phone className="mr-1.5 h-4 w-4" /> {t.listing.contact}
                    </a>
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Premium Spec Tiles */}
          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-white">{t.listing.details}</h2>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <SpecTile icon={Calendar} label={t.listing.spec.year} value={car.year} />
              <SpecTile icon={Gauge} label={t.listing.spec.km} value={`${fmtKM(car.mileage_km)} km`} />
              <SpecTile icon={Fuel} label={t.listing.spec.fuel} value={car.fuel} cap />
              <SpecTile icon={Cog} label={t.listing.spec.trans} value={car.transmission} cap />
              {car.body_type && <SpecTile icon={Car} label={t.listing.spec.body} value={car.body_type} cap />}
              {car.color && <SpecTile icon={Palette} label={t.listing.spec.color} value={car.color} cap />}
              {car.power_hp > 0 && <SpecTile icon={Zap} label={t.listing.spec.power} value={`${car.power_hp} PS`} />}
              {car.drivetrain && <SpecTile icon={Car} label={t.listing.spec.drivetrain} value={car.drivetrain} cap />}
            </div>
          </div>

          {/* Description */}
          {car.description && (
            <div className="mt-12">
              <h2 className="font-display text-xl font-semibold text-white">{t.listing.description}</h2>
              <p className="mt-4 whitespace-pre-line break-words text-base leading-relaxed text-zinc-300">{car.description}</p>
            </div>
          )}

          {/* Phase 69 — Premium "Condition & Trust" section */}
          <TrustSection car={car} locale={locale} />

          {/* Equipment */}
          {car.features && car.features.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-xl font-semibold text-white">{t.listing.equipment}</h2>
              <div className="mt-6 space-y-6">
                {FEATURE_CATEGORIES.map((cat) => {
                  const items = cat.items.filter((it) => car.features.includes(it));
                  if (items.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{cat.label.de}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`equipment-cat-${cat.id}`}>
                        {items.map((it) => (
                          <span
                            key={it}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-black/40 px-3 py-1 text-sm text-zinc-200"
                          >
                            <Check className="h-3 w-3 text-red-500" />
                            {it}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <SimilarListings listingId={car.id} sourceBrand={car.brand} sourceBody={car.body} />
          <RecentlyViewed excludeId={car.id} limit={6} />
        </div>

        {/* Sidebar — sticky on desktop, full content rendered everywhere */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.listing.spec.price}</p>
            <p className="mt-2 font-display text-4xl font-bold text-red-500">{fmtCHF(car.price_chf)}</p>
            <Badge className="mt-3 bg-zinc-800 text-zinc-300 hover:bg-zinc-800">{car.year} • {fmtKM(car.mileage_km)} km</Badge>

            <div className="mt-4">
              <PriceIndicator
                brand={car.brand}
                model={car.model}
                year={car.year}
                mileageKm={car.mileage_km}
                powerHp={car.power_hp}
                priceChf={car.price_chf}
                excludeId={car.id}
                compact
              />
            </div>

            <div className="mt-6 border-t border-zinc-800 pt-6">
              <p className="text-sm text-zinc-400">{t.listing.contact}</p>
              <p className="mt-1 font-display text-lg text-white">{car.owner_name}</p>
              {car.phone && (
                <a
                  href={`tel:${car.phone}`}
                  onClick={() => trackEvent("phone_click", { listing_id: car.id, source: "listing_detail" })}
                  className="mt-3 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                >
                  <Phone className="h-4 w-4" /> {car.phone}
                </a>
              )}
            </div>

            {!isOwner && (
              <div className="mt-6" id="inquiry-form">
                {/* Phase 66 — Guest fields (only when not logged in). Submits to
                     /guest-inquiry instead of /messages. */}
                {!user && (
                  <div className="mb-3 space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                    <p className="text-[11px] uppercase tracking-wider text-amber-300/80">
                      Schnellanfrage — ohne Konto
                    </p>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Dein Name"
                      maxLength={80}
                      autoComplete="name"
                      className="w-full rounded-md border border-zinc-800 bg-black px-3 py-2.5 text-base text-white placeholder:text-zinc-600 focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600/40"
                      data-testid="guest-name-input"
                    />
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="deine@email.ch"
                      autoComplete="email"
                      inputMode="email"
                      className="w-full rounded-md border border-zinc-800 bg-black px-3 py-2.5 text-base text-white placeholder:text-zinc-600 focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600/40"
                      data-testid="guest-email-input"
                    />
                    {/* Honeypot — must remain empty. Hidden visually + from screen readers. */}
                    <input
                      type="text"
                      value={guestHp}
                      onChange={(e) => setGuestHp(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                      data-testid="guest-hp-input"
                    />
                  </div>
                )}
                <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t.listing.placeholder} rows={4} className="border-zinc-800 bg-black text-white" data-testid="message-textarea" />
                <div className="mt-3">
                  <label htmlFor="inquiry-phone" className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-zinc-500">
                    <Phone className="h-3 w-3" /> {t.listing.phone_optional}
                  </label>
                  <input
                    id="inquiry-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t.listing.phone_placeholder}
                    maxLength={40}
                    className="w-full rounded-md border border-zinc-800 bg-black px-3 py-2.5 text-base text-white placeholder:text-zinc-600 focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600/40"
                    data-testid="inquiry-phone-input"
                  />
                  <p className="mt-1.5 text-[11px] text-zinc-500">{t.listing.phone_hint}</p>
                </div>
                <Button onClick={sendMsg} disabled={sending || !msg.trim()} className="mt-3 w-full bg-red-600 text-white hover:bg-red-500" data-testid="contact-seller-button">
                  {t.listing.send}
                </Button>
                <Button onClick={toggleFav} variant="outline" className="mt-2 w-full border-zinc-800 bg-transparent text-white hover:bg-zinc-900" data-testid="favorite-button">
                  <Heart className={`mr-2 h-4 w-4 ${isFav ? "fill-red-500 text-red-500" : ""}`} />
                  {isFav ? t.listing.unfavorite : t.listing.favorite}
                </Button>
                <CompareToggleButton listingId={car.id} variant="full" className="mt-2 w-full justify-center" />
              </div>
            )}
            {isOwner && (
              <div className="mt-6 space-y-2">
                <Button asChild className="w-full bg-red-600 text-white hover:bg-red-500"><Link to={`/dashboard`}>{t.dash.edit}</Link></Button>
                <CompareToggleButton listingId={car.id} variant="full" className="w-full justify-center" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE STICKY CTA BAR — only when scrolled past hero, only when not owner */}
      {!isOwner && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-800 bg-black/95 px-4 py-3 backdrop-blur-md lg:hidden"
          data-testid="mobile-sticky-cta"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-lg font-bold text-red-500 leading-tight">{fmtCHF(car.price_chf)}</p>
              <p className="truncate text-[11px] text-zinc-500">{car.owner_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleFav}
                aria-label={isFav ? t.listing.unfavorite : t.listing.favorite}
                className="grid h-11 w-11 place-items-center rounded-md border border-zinc-800 bg-zinc-950 text-white hover:bg-zinc-900"
                data-testid="mobile-cta-fav"
              >
                <Heart className={`h-4 w-4 ${isFav ? "fill-red-500 text-red-500" : ""}`} />
              </button>
              {car.phone && (
                <a
                  href={`tel:${car.phone}`}
                  aria-label="Anrufen"
                  className="grid h-11 w-11 place-items-center rounded-md border border-zinc-800 bg-zinc-950 text-white hover:bg-zinc-900"
                  data-testid="mobile-cta-call"
                >
                  <Phone className="h-4 w-4" />
                </a>
              )}
              <a
                href="#inquiry-form"
                onClick={(e) => { e.preventDefault(); document.getElementById("inquiry-form")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                className="inline-flex h-11 items-center gap-2 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-500"
                data-testid="mobile-cta-contact"
              >
                <MessageSquare className="h-4 w-4" /> {t.listing.send}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecTile({ icon: Icon, label, value, cap }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3.5">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
        <Icon className="h-3 w-3 text-red-500" /> {label}
      </div>
      <p className={`mt-1.5 font-display text-base font-semibold text-white ${cap ? "capitalize" : ""}`}>{value}</p>
    </div>
  );
}

function Spec({ icon, label, value, cap }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-zinc-500">{icon} {label}</div>
      <p className={`mt-1.5 text-base text-white ${cap ? "capitalize" : ""}`}>{value}</p>
    </div>
  );
}
